import { useEffect } from "react";
import {
  screenWallet,
  getRangeRiskConfig,
  type RangeRiskNetwork,
} from "../utils/range-risk";
import {
  screenSanctionsWallet,
  usesSanctionsOracle,
  SANCTIONS_COVERAGE_MESSAGE,
} from "../utils/wallet-screening";

type LogType = "info" | "success" | "error" | "warning";

type ScreeningStatus = "idle" | "checking" | "passed" | "blocked" | "error";

interface UseRangeScreeningParams {
  enabled: boolean;
  connected: boolean;
  address?: string | null;
  network?: RangeRiskNetwork;
  addLog: (message: string, type?: LogType) => void;
  setStatus: (status: {
    type: "success" | "error" | "info";
    message: string;
  }) => void;
  setScreeningStatus: (status: ScreeningStatus) => void;
  setScreeningMessage: (message: string | null) => void;
  setScreeningError: (message: string | null) => void;
  lastScreenedAddressRef: { current: string | null };
  screeningInFlightRef: { current: boolean };
}

export function useRangeScreening({
  enabled,
  connected,
  address,
  network = "base",
  addLog,
  setStatus,
  setScreeningStatus,
  setScreeningMessage,
  setScreeningError,
  lastScreenedAddressRef,
  screeningInFlightRef,
}: UseRangeScreeningParams) {
  useEffect(() => {
    if (!enabled) {
      setScreeningStatus("passed");
      setScreeningMessage("Compliance screening disabled.");
      setScreeningError(null);
      lastScreenedAddressRef.current = null;
      screeningInFlightRef.current = false;
      return;
    }
    if (!connected) {
      setScreeningStatus("idle");
      setScreeningMessage(null);
      setScreeningError(null);
      lastScreenedAddressRef.current = null;
      return;
    }

    const walletAddress = String(address || "").trim();
    if (!walletAddress) {
      setScreeningStatus("idle");
      setScreeningMessage(null);
      setScreeningError(null);
      lastScreenedAddressRef.current = null;
      return;
    }
    if (
      lastScreenedAddressRef.current === walletAddress &&
      screeningInFlightRef.current === false
    ) {
      return;
    }
    let cancelled = false;
    const controller = new AbortController();
    const runScreening = async () => {
      try {
        screeningInFlightRef.current = true;
        setScreeningStatus("checking");
        setScreeningMessage("Screening wallet for compliance...");
        setScreeningError(null);

        const oracle = usesSanctionsOracle();
        const result = oracle
          ? await screenSanctionsWallet(walletAddress, controller.signal)
          : await screenWallet(walletAddress, getRangeRiskConfig(), network);
        if (cancelled) return;

        lastScreenedAddressRef.current = walletAddress;

        if (result.blocked) {
          const reasonText = result.reasons.length
            ? result.reasons.join(" ")
            : "Wallet did not pass compliance screening.";
          setScreeningStatus("blocked");
          setScreeningMessage(`Compliance blocked: ${reasonText}`);
          setScreeningError(reasonText);
          addLog(`Compliance blocked: ${reasonText}`, "error");
          setStatus({
            type: "error",
            message: "Wallet blocked by compliance screening.",
          });
          return;
        }

        const riskScore =
          "risk" in result && typeof result.risk.riskScore === "number"
            ? result.risk.riskScore
            : "—";
        setScreeningStatus("passed");
        setScreeningMessage(
          oracle
            ? SANCTIONS_COVERAGE_MESSAGE
            : `Range screening passed (risk ${riskScore}, no sanctions match).`,
        );
        addLog(
          oracle
            ? SANCTIONS_COVERAGE_MESSAGE
            : `Range screening passed (risk ${riskScore}).`,
          "success",
        );
      } catch (err) {
        if (cancelled) return;
        const msg = err instanceof Error ? err.message : String(err);
        setScreeningStatus("error");
        setScreeningMessage(`Compliance check failed: ${msg}`);
        setScreeningError(msg);
        addLog(`Compliance check failed: ${msg}`, "warning");
        setStatus({
          type: "error",
          message: "Compliance check failed. Please reconnect.",
        });
      } finally {
        if (!cancelled) screeningInFlightRef.current = false;
      }
    };

    runScreening();
    return () => {
      cancelled = true;
      controller.abort();
      screeningInFlightRef.current = false;
    };
  }, [
    enabled,
    connected,
    address,
    network,
    addLog,
    setStatus,
    setScreeningStatus,
    setScreeningMessage,
    setScreeningError,
    lastScreenedAddressRef,
    screeningInFlightRef,
  ]);
}
